import { isDefined } from "@clipboard-health/util-ts";

import { DocumentStatus } from "../../../../../Accounts/Documents/types";
import { APP_V2_APP_EVENTS, logError } from "../../../../../lib/analytics";
import { IGNORED_ESIGN_WORKFLOW_SESSION_STATUSES } from "./constants";
import { type ESignWorkflowSessionResponse } from "./types";
import { fetchESignWorkflowSessions } from "./useSignableESignWorkflowSession";

function getDocumentStatusOfESignWorkflowSessionFromData(
  data: ESignWorkflowSessionResponse["data"] | undefined,
  included: ESignWorkflowSessionResponse["included"]
): DocumentStatus | undefined {
  if (isDefined(included) && isDefined(data?.relationships?.document)) {
    return included?.find((item) => item.id === data?.relationships?.document?.data?.id)?.attributes
      .status;
  }
  return undefined;
}

// Accepts either pre-fetched response data or identifiers to fetch fresh data.
// The fetch fallback exists because the PATCH (sign) call can return a 409
// when a webhook updates the session status before the PATCH completes.
export async function getDocumentStatusOfESignWorkflowSession(
  params:
    | {
        data: ESignWorkflowSessionResponse["data"];
        included: ESignWorkflowSessionResponse["included"];
      }
    | {
        requirementId: string;
        workerId: string;
      }
): Promise<DocumentStatus | undefined> {
  try {
    if ("data" in params) {
      const { data, included } = params;
      return getDocumentStatusOfESignWorkflowSessionFromData(data, included);
    }

    if ("requirementId" in params && "workerId" in params) {
      const { requirementId, workerId } = params;
      const response = await fetchESignWorkflowSessions({
        requirementId,
        workerId,
        filter: {
          statuses: {
            ne: [...IGNORED_ESIGN_WORKFLOW_SESSION_STATUSES],
          },
        },
      });
      const [data] = response.data;
      const { included } = response;
      return getDocumentStatusOfESignWorkflowSessionFromData(data, included);
    }
  } catch (error: unknown) {
    logError(APP_V2_APP_EVENTS.PANDADOC_SESSION_EXCEPTION, {
      error,
    });
  }
  return undefined;
}
